import { Download, RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { formatBytes } from '../../../../lib/formatBytes';
import { UploadFileModal } from '../components/UploadFileModal';
import { filesApi } from '../filesApi';
import type { StoredFileListItem } from '../fileTypes';

/** Stored files — same shell, different columns. Reference §3. */
export function FilesIndexPage() {
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState<{ kind: 'deactivate' | 'activate'; file: StoredFileListItem } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchFiles = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      filesApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [],
  );

  const download = useCallback(async (file: StoredFileListItem) => {
    setError(null);

    try {
      const { blob, fileName } = await filesApi.download(file.id);

      /*
        An object URL, revoked immediately after the click. The alternative — pointing an
        anchor straight at the endpoint — would drop the Authorization header, since a
        browser navigation carries no bearer token.
      */
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');

      anchor.href = url;
      anchor.download = fileName ?? file.fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      URL.revokeObjectURL(url);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The file could not be downloaded.', 0));
    }
  }, []);

  const rowActions = useCallback(
    (file: StoredFileListItem): MenuItem[] => [
      {
        key: `download-${file.id}`,
        label: 'Download',
        icon: <Download className="h-4 w-4" aria-hidden="true" />,
        permission: 'files.download',
        disabledReason: file.isActive ? null : 'Restore this file before downloading it.',
        onClick: () => void download(file),
      },
      {
        key: `restore-${file.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'files.restore',
        disabledReason: file.isActive ? 'This file is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', file }),
      },
    ],
    [download],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') await filesApi.deactivate(confirming.file.id);
      else await filesApi.activate(confirming.file.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<StoredFileListItem>
        module="files"
        breadcrumb={['Administration', 'Files']}
        columns={COLUMNS}
        facets={[
          { key: 'status', label: 'Status' },
          { key: 'category', label: 'Category' },
        ]}
        auditEntityTypes={['StoredFile']}
        rowKey={(file) => file.id}
        fetch={fetchFiles}
        reloadKey={reloadKey}
        searchPlaceholder="Search files"
        emptyMessage="No files uploaded yet."
        onCreate={() => setUploading(true)}
        createPermission="files.create"
        rowActions={rowActions}
        onDelete={(file) => setConfirming({ kind: 'deactivate', file })}
        deletePermission="files.delete"
        deleteDisabledReason={(file) => (file.isActive ? null : 'This file is already deleted.')}
        onClearData={() => filesApi.clear()}
        clearDataPermission="files.clear-data"
        otherInfo={(file) => ({
          createdAt: file.createdAt,
          createdByName: file.createdByName,
          updatedAt: file.updatedAt,
          updatedByName: file.updatedByName,
        })}
      />

      {uploading ? (
        <UploadFileModal open onClose={() => setUploading(false)} onUploaded={refresh} />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete file' : 'Restore file'}
          danger={confirming.kind === 'deactivate'}
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`file-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.file.fileName}" will stop being downloadable. Its stored contents are kept, so this can be undone.`
                  : `"${confirming.file.fileName}" will be downloadable again.`}
              </p>
              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}

    </>
  );
}

/**
 * Column definitions. `File` is the frozen anchor and carries the soft flex.
 *
 * One column, one value — the hash and the size are their own columns rather than a second
 * line under the name, and status is last. See CLAUDE.md, "Table column conventions".
 */
const COLUMNS: ColumnDef<StoredFileListItem>[] = [
  {
    key: 'fileName',
    header: 'File',
    width: 280,
    flex: true,
    sortable: true,
    render: (file) => <TextCell value={file.fileName} />,
  },
  {
    key: 'category',
    header: 'Category',
    width: 170,
    sortable: true,
    render: (file) => <TextCell value={file.categoryName} />,
  },
  {
    key: 'contentType',
    header: 'Type',
    width: 150,
    render: (file) => <TextCell value={file.contentType} mono />,
  },
  {
    key: 'sizeBytes',
    header: 'Size',
    width: 110,
    align: 'right',
    sortable: true,
    render: (file) => <TextCell value={formatBytes(file.sizeBytes)} mono numeric />,
  },
  {
    // Truncated for the column, full value in the title. The whole digest is what makes it
    // verifiable, so it stays reachable rather than being rounded off.
    key: 'sha256',
    header: 'SHA-256',
    width: 180,
    render: (file) => <TextCell value={`${file.sha256.slice(0, 16)}…`} mono />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (file) =>
      file.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
